/** @format */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import "./News.css";
import { gridNewsStyle } from "../../utils/Styles/GridNewsStyle";
import newsIcon from "../../img/icons/news.png";

const NewsGrid = () => {
  const [gridNews, setGridNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const isManualNavigationRef = useRef(false);
  const newsContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const handleBeforeUnload = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const formatRelativeTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return "Data inválida";

      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      const minutes = Math.floor(diffInSeconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);

      if (weeks > 0) return `há ${weeks} ${weeks === 1 ? "semana" : "semanas"}`;
      if (days > 0) return `há ${days} ${days === 1 ? "dia" : "dias"}`;
      if (hours > 0) return `há ${hours} ${hours === 1 ? "hora" : "horas"}`;
      if (minutes > 0)
        return `há ${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;

      return "agora mesmo";
    } catch (error) {
      return "Data inválida";
    }
  };

  const resolveImageUrl = (path) =>
    `https://iscompatible-api.com.br/api/v1/static/${path.replace(/^\/?static\//, "")}`;

  const ImageRenderer = ({ src, alt }) => {
    if (!src) return null;

    const fullSrc = src.startsWith("http") ? src : resolveImageUrl(src);

    return (
      <div className="markdown-image-container">
        <img
          src={fullSrc}
          alt={alt || "Imagem da notícia"}
          className="markdown-image"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>
    );
  };

  const handlePageChange = (newPage) => {
    isManualNavigationRef.current = true;
    setPage(newPage);
  };

  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        setLoading(true);

        const gridResponse = await axios.get(
          `https://iscompatible-api.com.br/api/v1/news/summaries?page=${page}&limit=10`
        );

        const sortedGridNews = gridResponse.data
          .filter((item) => item.title?.trim())
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setGridNews(sortedGridNews);

        const totalCountResponse = await axios.get(
          `https://iscompatible-api.com.br/api/v1/news/count`
        );

        const totalItems = totalCountResponse.data.count;
        const calculatedTotalPages = Math.ceil(totalItems / 10);
        setTotalPages(calculatedTotalPages);
      } catch (error) {
        return "Erro ao buscar notícias: " + error.message;
      } finally {
        setLoading(false);

        requestAnimationFrame(() => {
          if (isManualNavigationRef.current) {
            const titleElement = document.querySelector(".modern-news-title");
            if (titleElement) {
              const offset = 100;
              const position =
                titleElement.getBoundingClientRect().top +
                window.pageYOffset -
                offset;
              window.scrollTo({
                top: position,
                behavior: "smooth",
              });
            }
          } else {
            window.scrollTo({
              top: 0,
              behavior: "auto",
            });
          }

          isManualNavigationRef.current = false;
        });
      }
    };

    fetchNewsData();
  }, [page]);

  const handleNewsClick = (newsItem) => {
    const path = newsItem.slug
      ? `/news/${newsItem.slug}`
      : `/news/${newsItem.id}`;
    navigate(path);
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="modern-news-container" ref={newsContainerRef}>
      <div className="modern-news-header">
        <img src={newsIcon} alt="" className="img-icon-news" />
        <h1 className="modern-news-title pt-4">Notícias</h1>
      </div>

      {gridNews.length > 0 && (
        <div className="modern-news-grid" style={gridNewsStyle}>
          {gridNews.map((item) => (
            <div
              key={`${item.slug || item.id}-${item.id}`}
              className="modern-news-card"
              onClick={() => handleNewsClick(item)}
              tabIndex={0}>
              {item.thumbnail && (
                <div className="modern-news-image-container">
                  <img
                    src={resolveImageUrl(item.thumbnail)}
                    alt={item.title || "Notícia sem título"}
                    className="modern-news-image"
                  />
                </div>
              )}
              <div className="modern-news-content">
                <h3 className="modern-news-heading">
                  {item.title || "Notícia sem título"}
                </h3>
                <div className="modern-subtitle">
                  {item.subtitle ? (
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw]}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        img: ImageRenderer,
                        table: ({ node, ...props }) => (
                          <div className="table-container">
                            <table {...props} />
                          </div>
                        ),
                        h1: ({ node, ...props }) => <h3 {...props} />,
                        h2: ({ node, ...props }) => <h4 {...props} />,
                      }}
                      disallowedElements={["img", "iframe", "table"]}
                      unwrapDisallowed={true}>
                      {item.subtitle.length > 200
                        ? `${item.subtitle.substring(0, 300)}...`
                        : item.subtitle}
                    </ReactMarkdown>
                  ) : (
                    <p>Nenhum resumo disponível</p>
                  )}
                </div>
                <div className="modern-news-meta">
                  <span className="modern-date-grid" title={item.created_at}>
                    {formatRelativeTime(item.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {gridNews.length > 0 && (
        <div className="modern-pagination">
          <button
            className="modern-pagination-button"
            onClick={() => handlePageChange(Math.max(1, page - 1))}
            disabled={page === 1 || loading}>
            &larr; Anterior
          </button>

          <span className="modern-page-number">
            Página {page} de {totalPages}
          </span>

          <button
            className="modern-pagination-button"
            onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || loading}>
            Próxima &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsGrid;
